import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";

import DashboardLayout from "components/layout/DashboardLayout";
import DashboardNavbar from "components/navigation/DashboardNavbar";
import V2PageHeader from "components/V2PageHeader";
import VuiBox from "components/ui/VuiBox";
import VuiTypography from "components/ui/VuiTypography";

// New reusable components (built M005/S02)
import InlineBriefing from "components/InlineBriefing";
import TopicTagsStrip from "components/TopicTagsStrip";
import PressArticleCard from "components/PressArticleCard";

// Data hooks
import useInlineBriefingData from "components/InlineBriefing/useInlineBriefingData";
import usePressTopics from "./usePressTopics";
import usePressArticleFeed from "./usePressArticleFeed";

// Context
import { useAnalytics } from "context/AnalyticsContext";
import { usePress } from "context/PressContext";
import StandardMessageDisplay from "components/StandardMessageDisplay";
import { GRADIENTS, ACCENT_COLORS } from "utils/designTokens";

/**
 * PressDigestV2
 *
 * Complete redesign of the Press Digest page. Admin-only preview (M005/S02).
 * Route: /v2/press-digest    adminOnly: true
 *
 * Layout:
 *   1. Header — source selector + article count
 *   2. InlineBriefing — auto-triggered AI summary (no button/modal)
 *   3. TopicTagsStrip — interactive filter pills from analysis.topic
 *   4. Article feed — PressArticleCard per article, "Load more" at bottom
 */
function PressDigestV2() {

  const { selectedPresetKey } = useAnalytics();
  const { selectedPress, setSelectedPress, pressOptions, loading: pressLoading } = usePress();
  const [selectedTopic, setSelectedTopic] = useState(null);

  // AI briefing — auto-triggered
  const briefingParams = {
    preset: selectedPresetKey?.toUpperCase(),
    ...(selectedPress ? { source: selectedPress } : {}),
  };
  const {
    briefing,
    loading: briefingLoading,
    error: briefingError,
  } = useInlineBriefingData(
    selectedPresetKey ? "/v1/reports/news-catchup" : null,
    briefingParams
  );

  // Extract notableNewsSummary from the response shape.
  const rawBriefingText = briefing?.notableNewsSummary ?? briefing;
  const briefingText = (() => {
    if (!rawBriefingText) return rawBriefingText;

    // Fallback: convert bare ISO dates (2026-03-03) emitted by cached/older responses
    // to readable form. New responses will have "March 3, 2026" from the prompt.
    let text = rawBriefingText.replace(
      /\b(\d{4})-(\d{2})-(\d{2})\b/g,
      (_, y, m, d) => new Date(`${y}-${m}-${d}T12:00:00`)
        .toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
    );

    // When a single source is selected, strip the source name prefix from bullets.
    // The LLM always includes it (per the prompt) but the user already knows which
    // source they picked. Handle all patterns the LLM produces:
    //   "**March 3, 2026** SourceName: text"  → "**March 3, 2026** text"
    //   "**SourceName: text"                  → "text"  (bold-opens the line, no date)
    //   "SourceName: text"                    → "text"
    if (selectedPress) {
      const sourceName = pressOptions?.find(p => p.id === selectedPress)?.name;
      if (sourceName) {
        const esc = sourceName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        text = text
          .replace(new RegExp(`(\\*\\*[^*]+\\*\\*)\\s+${esc}:\\s*`, "gi"), "$1 ")
          .replace(new RegExp(`\\*\\*${esc}:\\s*`, "gi"), "")
          .replace(new RegExp(`${esc}:\\s*`, "gi"), "")
          .replace(/\*\*\s*\*\*/g, "")
          .trim();
      }
    }

    return text;
  })();

  // Topic tags
  const { topics, loading: topicsLoading } = usePressTopics(selectedPress, selectedPresetKey);

  // Article feed — resets when topic filter changes
  const {
    articles,
    loading: articlesLoading,
    error: articlesError,
    hasMore,
    loadMore,
    totalElements,
  } = usePressArticleFeed(selectedPress, selectedPresetKey, selectedTopic);

  // Reset topic filter when source changes
  useEffect(() => {
    setSelectedTopic(null);
  }, [selectedPress]);

  const handleTopicSelect = (topic) => {
    setSelectedTopic(prev => prev === topic ? null : topic);
  };

  const selectedPressLabel = pressOptions?.find(p => p.id === selectedPress)?.name ?? "All Sources";

  return (
    <DashboardLayout navbarHeight={88}>
      <DashboardNavbar noSelectors />
      <VuiBox pt={0} pb={3}>

        {/* Page header */}
        <V2PageHeader
          title="Press Digest"
          subtitle={!articlesLoading && totalElements > 0
            ? `${totalElements} article${totalElements !== 1 ? "s" : ""} · ${selectedPressLabel}${selectedTopic ? ` · ${selectedTopic}` : ""}`
            : null}
        />

        {/* ── SOURCE SELECTOR pills ── */}
        {!pressLoading && pressOptions?.length > 0 && (
          <VuiBox mb={3}>
            <VuiBox
              sx={{
                display: "flex",
                gap: 1,
                overflowX: "auto",
                pb: 0.5,
                "&::-webkit-scrollbar": { height: "3px" },
                "&::-webkit-scrollbar-thumb": { background: "rgba(255,255,255,0.15)", borderRadius: "4px" },
              }}
            >
              {pressOptions.map((option) => {
                const isActive = selectedPress === option.id;
                return (
                  <VuiBox
                    key={option.id ?? "all"}
                    onClick={() => setSelectedPress(option.id)}
                    sx={{
                      px: 2,
                      py: 0.75,
                      borderRadius: "20px",
                      border: `1px solid ${isActive ? ACCENT_COLORS.primary : "rgba(255,255,255,0.12)"}`,
                      background: isActive ? "rgba(37,99,235,0.2)" : "rgba(255,255,255,0.05)",
                      cursor: "pointer",
                      flexShrink: 0,
                      transition: "all 0.18s ease",
                      "&:hover": {
                        background: isActive ? "rgba(37,99,235,0.28)" : "rgba(255,255,255,0.09)",
                      },
                      userSelect: "none",
                    }}
                  >
                    <VuiTypography
                      variant="caption"
                      color="white"
                      fontWeight={isActive ? "bold" : "regular"}
                      sx={{ fontSize: "0.8rem", whiteSpace: "nowrap" }}
                    >
                      {option.name}
                    </VuiTypography>
                  </VuiBox>
                );
              })}
            </VuiBox>
          </VuiBox>
        )}

        {/* ── ZONE 1: INLINE AI BRIEFING ── */}
        <VuiBox mb={3}>
          <InlineBriefing
            briefing={briefingText}
            loading={briefingLoading}
            error={briefingError}
            title={selectedPressLabel !== "All Sources"
              ? `What's new from ${selectedPressLabel}`
              : "What's new in neurovascular"}
            minHeight={200}
          />
        </VuiBox>

        {/* ── ZONE 2: TOPIC TAGS STRIP ── */}
        {(topics.length > 0 || topicsLoading) && (
          <VuiBox mb={3}>
            <TopicTagsStrip
              topics={topics}
              selected={selectedTopic}
              onSelect={handleTopicSelect}
              loading={topicsLoading}
            />
          </VuiBox>
        )}

        {/* ── ZONE 3: ARTICLE FEED ── */}
        <VuiBox>
          {articlesError ? (
            <StandardMessageDisplay
              type="error"
              title="Could not load articles"
              message="Please try refreshing the page."
              containerHeight="300px"
            />
          ) : articles.length === 0 && !articlesLoading ? (
            <StandardMessageDisplay
              type="noData"
              title="No articles found"
              message={selectedTopic
                ? `No articles tagged "${selectedTopic}" this period. Try a different topic or clear the filter.`
                : "No press articles were found for the selected source and period."}
              containerHeight="200px"
            />
          ) : (
            <VuiBox sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {articles.map((article, i) => (
                <PressArticleCard
                  key={article.id ?? article.url ?? i}
                  title={article.title}
                  url={article.url}
                  summary={article.analysis?.summary}
                  topic={article.analysis?.topic}
                  source={article.source}
                  date={article.date}
                  productMentions={article.analysis?.productMentions}
                  onTopicClick={handleTopicSelect}
                />
              ))}

              {/* Load more / spinner */}
              {articlesLoading && (
                <VuiBox display="flex" justifyContent="center" py={3}>
                  <CircularProgress size={24} sx={{ color: ACCENT_COLORS.highlight }} />
                </VuiBox>
              )}

              {hasMore && !articlesLoading && (
                <VuiBox display="flex" justifyContent="center" pt={1} pb={3}>
                  <VuiBox
                    onClick={loadMore}
                    sx={{
                      px: 3,
                      py: 1,
                      borderRadius: "20px",
                      border: "1px solid rgba(255,255,255,0.15)",
                      background: "rgba(255,255,255,0.05)",
                      cursor: "pointer",
                      transition: "all 0.18s ease",
                      "&:hover": {
                        background: "rgba(255,255,255,0.09)",
                        borderColor: "rgba(255,255,255,0.25)",
                      },
                    }}
                  >
                    <VuiTypography
                      variant="caption"
                      sx={{ color: "rgba(255,255,255,0.6)", fontSize: "0.82rem" }}
                    >
                      Load more articles
                    </VuiTypography>
                  </VuiBox>
                </VuiBox>
              )}
            </VuiBox>
          )}
        </VuiBox>

      </VuiBox>
    </DashboardLayout>
  );
}

export default PressDigestV2;
